/**
 * SafeHtml — sanitize untrusted HTML (CMS / API / i18n) before render.
 * Uses isomorphic-dompurify so SSR and client share the same clean markup.
 */

import type { ElementType, HTMLAttributes } from 'react';
import DOMPurify from 'isomorphic-dompurify';

const DEFAULT_ALLOWED_TAGS = [
  'p',
  'span',
  'strong',
  'em',
  'ul',
  'ol',
  'li',
  'a',
  'br',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'img',
  'table',
  'thead',
  'tbody',
  'tr',
  'td',
  'th',
] as const;

const DEFAULT_ALLOWED_ATTR = [
  'href',
  'target',
  'rel',
  'src',
  'alt',
  'class',
  'colspan',
  'rowspan',
] as const;

export interface SafeHtmlProps extends Omit<HTMLAttributes<HTMLElement>, 'dangerouslySetInnerHTML'> {
  html: string;
  allowedTags?: string[];
  allowedAttributes?: string[];
  /** Element tag to render. Defaults to `div`. */
  as?: ElementType;
}

export function SafeHtml({
  html,
  allowedTags,
  allowedAttributes,
  as: Tag = 'div',
  ...rest
}: Readonly<SafeHtmlProps>) {
  // Do not set USE_PROFILES — DOMPurify profiles override ALLOWED_TAGS/ATTR.
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: allowedTags ?? [...DEFAULT_ALLOWED_TAGS],
    ALLOWED_ATTR: allowedAttributes ?? [...DEFAULT_ALLOWED_ATTR],
  });

  return <Tag {...rest} dangerouslySetInnerHTML={{ __html: clean }} />;
}
